import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { UserLocation } from '../../models/user/location.model';
import { AnalyticsService } from '../analytics';
import { RumService } from '@spotlight/shared/rum';
import { clone } from 'ramda';
import * as fromRoot from '../../reducers';
import * as user from '../../actions/user';
import * as settings from '../../actions/settings';
import { ErrorHandlerService } from '../error-handler';
import { environment } from 'spotlight-ui/environments/environment';
import { EulaFoundAction } from '../../actions/eula';
import { AppConstants } from '../../app.constants';
import { LaunchDarklyService } from 'spotlight-ui/app/launch-darkly.service';
import { User } from 'spotlight-ui/app/models/user/user.model';
import { extractLocationNumberFromPath, isLocationAvailable, resolveDefaultLocation } from 'spotlight-ui/app/shared/navbar/location-utils';
import { LocationScenarioService } from '../location-scenario';
import { SettingsService } from 'spotlight-ui/app/settings/settings.service';

@Injectable()
export class UserService {
  public selectedLocation$: Observable<UserLocation>;
  private selectedLocationSource: ReplaySubject<UserLocation>;
  private userData!: User;
  private selectedLocation!: UserLocation;

  public setupUser(version: string) {
    return new Promise((resolve) => {
      this.http.get(environment.spotlightApiUrl + '/api/user').pipe(
        switchMap((response: any) => {
          let userData = response.responseObject as User;
          userData = { ...userData, locations: userData.locations.filter(location => location.locationNumber !== '00000') };
          // Favorite locations live behind their own endpoint, not on /api/user - fetch them
          // here so the location pickers can sort favorites to the top from their very first
          // render, instead of flashing an unsorted list until the separate Settings-page
          // load (effects/settings.ts) resolves.
          return this.settingsService.loadUserSettings(userData.userName).pipe(
            catchError(() => of({})),
            map((userSettings: settings.UserSettingsPayload) => ({ userData, userSettings }))
          );
        }),
        catchError((error: any) => {
          //only place error action dispatched before the error handling service can handle, so do message logic here
          let message = error.error ?
            error.error.message ? error.error.message : error.error :
            error.message ? error.message : AppConstants.genericApiError;
          this.store.dispatch(new user.UserDataErrorAction(message));
          resolve(error);
          return EMPTY;
        })
      ).subscribe(({ userData, userSettings }: { userData: User; userSettings: settings.UserSettingsPayload }) => {
        // Admin simulator (non-production only): flag locations client-side, before anything
        // reads them, so guards and default selection behave as they would for a real failure.
        userData = this.locationScenario.apply(userData, userSettings?.favoriteLocations);
        if (this.locationScenario.isSimulating) {
          this.rum.setGlobalContextProperty('simulated', true);
        }
        this.analytics.setup(userData);
        this.rum.setUser(userData.userName);
        this.rum.setGlobalContextProperty('userType', userData.userType);
        this.store.dispatch(new user.UserDataFoundAction(clone(userData)));
        this.store.dispatch(new settings.LoadSettingsSuccessAction(userSettings));
        this.userData = { ...userData, userSettings: { ...userData.userSettings, ...userSettings } };
        this.selectDefaultLocation(this.userData);
        resolve(true);
      });
    });
  }

  public getEula(): Promise<any> {
    return new Promise((resolve) => {
      this.http.get(environment.spotlightApiUrl + '/api/eula/accept').pipe(
        catchError((error: any) => {
          resolve(error);
          return this.ehs.handleError(error);
        }))
        .subscribe((eulaData: any) => {
          this.store.dispatch(new EulaFoundAction(eulaData.responseObject));
          resolve(true);
        });
    });
  }

  constructor(private http: HttpClient,
              private store: Store<fromRoot.State>,
              private ehs: ErrorHandlerService,
              private injector: Injector,
              private analytics: AnalyticsService,
              private readonly ldService: LaunchDarklyService,
              private readonly settingsService: SettingsService,
              private readonly locationScenario: LocationScenarioService,
              private readonly rum: RumService) {
    this.selectedLocationSource = new ReplaySubject(1);
    this.selectedLocation$ = this.selectedLocationSource.asObservable();
  }

  public getCurrentSelectedLocation(): UserLocation | undefined {
    return this.selectedLocation;
  }

  public getSelectedLocation(): Observable<any> {
    if (this.selectedLocation) {
      return of(this.selectedLocation);
    } else {
      return this.getUser().pipe(map(() => this.selectedLocation));
    }
  }

  public updateSelectedLocation(location: UserLocation) {
    this.analytics.setLocationAttributes(location);
    this.rum.setGlobalContextProperty('locationNumber', location.locationNumber);
    this.selectedLocation = location;
    this.ldService.initializeClient(location.locationNumber, this.userData.userName).then((flags) => {
      this.rum.setFeatureFlags(flags);
      this.store.dispatch(new user.FeatureFlagsSet(flags));
      this.store.select(fromRoot.getFeatureEnabled(AppConstants.maintenanceFlag)).pipe(take(1)).subscribe(activeMaintenance => {
        if (!window.location.href.includes('/eula') && !activeMaintenance) {
          this.store.dispatch(new user.SelectLocation(location));
        }
        this.selectedLocationSource.next(location);
      });
    });

  }

  public dismissAlert(alertId: string) {
    return this.getSelectedLocation().pipe(
      switchMap((loc) => {
        const url = `/api/location/${loc.locationNumber}/user/alert/${alertId}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      })
    );
  }

  public updateBannerSettings(bannerId: string) {
    return this.getUser().pipe(
      switchMap((user) => {
        const url = `/api/user/banner/${bannerId}/${user.userName}`;
        return this.http.delete(environment.spotlightApiUrl + url);
      }),
      map((response: any) => {
        return bannerId;
      }),
      catchError((error: any) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getUser(): Observable<User> {
    return of(this.userData);
  }

  public getLocationDetails(location: UserLocation): Observable<UserLocation> {
    return this.http.get(environment.spotlightApiUrl + `/api/location/${location.locationNumber}`).pipe(
      map((res: any) => res),
      catchError((err: any) => {
        if (this.isOperatorNotFoundError(err)) {
          return of(this.withOperatorPlaceholder(location));
        }
        return this.ehs.handleError(err);
      })
    );
  }

  public getDiscoverButtons(): Observable<any> {
    return this.http.get(environment.spotlightApiUrl + '/api/discover/discoverButtons').pipe(
      map((res:any) => res),
      catchError((err:any) => this.ehs.handleError(err))
    );
  }

  private selectDefaultLocation(userData: User): UserLocation {
    const urlLocationNumber = extractLocationNumberFromPath(globalThis.location.pathname);
    const urlLocation = urlLocationNumber
      ? userData.locations.find((loc) => loc.locationNumber === urlLocationNumber)
      : undefined;
    const defaultLocation = urlLocation
      ?? resolveDefaultLocation(userData.locations, userData.userSettings?.favoriteLocations);
    // Selecting an unavailable location would fetch its details (which 500). The routing guards
    // send the user to the location error page instead.
    if (isLocationAvailable(defaultLocation)) {
      this.updateSelectedLocation(defaultLocation);
    }
    return defaultLocation;
  }

  private isOperatorNotFoundError(error: any): boolean {
    const messages = this.collectErrorMessages(error?.error?.errors ?? error?.errors ?? error?.error ?? error);
    return messages.some(message => message.toLowerCase().includes('operator not found'));
  }

  private collectErrorMessages(value: unknown): string[] {
    if (!value) {
      return [];
    }
    if (typeof value === 'string') {
      return [value];
    }
    if (Array.isArray(value)) {
      return value.flatMap(entry => this.collectErrorMessages(entry));
    }
    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).flatMap(entry => this.collectErrorMessages(entry));
    }
    return [];
  }

  private withOperatorPlaceholder(location: UserLocation): UserLocation {
    return {
      ...location,
      operatorImageUrl: location?.operatorImageUrl || AppConstants.operatorPlaceholderImageUrl,
      operatorName: location?.operatorName || ''
    };
  }
}
